import type { FlagGrant, FlagVerdictSource } from '../schemas/flag';
import type { TenantFlagView } from '../schemas/tenant-flag';

/** What this row's switch does when clicked. */
export type TenantFlagToggle =
    | { readonly action: 'set'; readonly enabled: boolean }
    /** Withdraws the last grant, so the museum falls back to the default. */
    | { readonly action: 'clear' }
    /**
     * Withdraws the hand-made grant while a product or component keeps holding
     * the row up. The flag stays exactly where it is, and `remaining` says what
     * is keeping it there.
     */
    | { readonly action: 'withdraw'; readonly remaining: readonly FlagGrant[] }
    | { readonly action: 'none'; readonly reason: 'archived' };

export interface TenantFlagRow {
    readonly key: string;
    readonly name: string;
    readonly category: string;
    /** What this museum actually gets — the verdict, never a stored `enabled`. */
    readonly enabled: boolean;
    readonly source: FlagVerdictSource;
    /** Whether the value comes from this museum's own row or the platform default. */
    readonly origin: 'override' | 'default';
    /** The value resolved through a percentage gate, so it is not absolute. */
    readonly gated: boolean;
    readonly toggle: TenantFlagToggle;
    /**
     * Whether the switch must stop for a confirmation before it writes.
     *
     * True on exactly one case — creating this museum's first override — which
     * is the line `FlagDetailPage` already draws: `OverrideDialog` confirms
     * (NOXSCRUM-1670), its override rows' toggles and its "Revert to default"
     * button do not.
     */
    readonly confirms: boolean;
}

/**
 * One row of the per-client flags table, and what its switch does.
 *
 * Pure, and separate from the component, because `apps/web` runs vitest on
 * `node` with no jsdom: the decisions worth pinning have to be reachable as
 * data or they are not tested at all.
 *
 * ## Why turning a flag back is a delete
 *
 * `PUT`ting the default's own value onto an override leaves a row behind that
 * says "this museum is pinned here" when nobody decided that — and the next
 * platform-wide default change would then skip this museum in silence. So when
 * the switch would land exactly on what the museum would inherit, the action is
 * `clear`.
 *
 * "Exactly" is doing work: it holds only while neither row carries a rollout
 * gate. An override that drops a 25% default gate is a real change, and
 * collapsing it into a delete would restore the very gate the operator was
 * removing. Gated rows stay a write.
 *
 * ## And why a delete is no longer always a delete
 *
 * The row records what is holding it up (ADR 0010 §2), and clearing withdraws
 * the **hand-made** grant rather than removing the row. When a product or a
 * component still grants the flag, the row survives and the flag stays on — so
 * the switch is not turning anything off, and `withdraw` says that instead of
 * `clear` promising something it will not do.
 *
 * Nothing here depends on who is looking. A grant names a source, not a person,
 * so the manual grant is the same one for everybody and any operator can undo
 * what another did.
 */
export function tenantFlagRow(view: TenantFlagView): TenantFlagRow {
    const { definition, default: defaultRow, override, verdict } = view;

    const base = {
        key: definition.key,
        name: definition.name,
        category: definition.category,
        enabled: verdict.enabled,
        source: verdict.source,
        origin: override ? ('override' as const) : ('default' as const),
        gated: verdict.source === 'rollout',
    };

    // Archived is the real kill switch: the flag is never served, override or
    // not. A live switch here would report the opposite of what the platform
    // does, so the row refuses and names the reason for the UI to render.
    if (defaultRow.archived) {
        return { ...base, toggle: { action: 'none', reason: 'archived' }, confirms: false };
    }

    const target = !verdict.enabled;
    const ungated =
        override?.rolloutPercentage === undefined && defaultRow.rolloutPercentage === undefined;

    if (override && ungated && target === defaultRow.enabled) {
        // `undefined` grants mean the row predates provenance: it has no
        // grantors to count, and clearing it deletes it as it always did.
        const remaining = (override.grantedBy ?? []).filter((grant) => grant.kind !== 'manual');
        return remaining.length > 0
            ? { ...base, toggle: { action: 'withdraw', remaining }, confirms: true }
            : { ...base, toggle: { action: 'clear' }, confirms: false };
    }

    // Only the first write for this museum stops. Once the row exists, its
    // value has already been agreed to once, and asking again on every flip
    // would be stricter than the flag screen for no added protection.
    return { ...base, toggle: { action: 'set', enabled: target }, confirms: override === null };
}
